/**
 * Formats a GCS bucket region for display, taking into account the bucket's
 * location type:
 *   - regional:                "us-central1"
 *   - multi-region:            "US (multi-region)"
 *   - custom dual-region:      "us-east1, us-west1 (dual-region)"
 *   - predefined dual-region:  "NAM4 (dual-region)"
 *   - zonal:                   "us-central1 (zonal)"
 */
export const formatGcsRegion = (
  regionName: string | undefined,
  locationType: string | undefined,
  constituentRegions: string[] | undefined
): string => {
  if (!regionName) {
    return '';
  }

  // The GCS API returns regional and zonal locations uppercase (EUROPE-WEST3); other GCP resources use lowercase.
  if (locationType === 'region') {
    return regionName.toLowerCase();
  }

  if (locationType === 'zone') {
    return `${regionName.toLowerCase()} (zonal)`;
  }

  if (locationType === 'multi-region') {
    return `${regionName} (multi-region)`;
  }

  if (locationType === 'dual-region') {
    if (constituentRegions && constituentRegions.length > 0) {
      return `${constituentRegions.join(', ')} (dual-region)`;
    }
    return `${regionName} (dual-region)`;
  }

  return regionName;
};

/**
 * Constituent regions for predefined GCS dual-region locations. Mirror of the
 * Go-side `dualRegionFallbackRegions` map in `shared/gcp/gcs/gcs_client.go`.
 * See https://cloud.google.com/storage/docs/locations#location-mr.
 */
export const PREDEFINED_DUAL_REGION_CONSTITUENTS: Record<string, string[]> = {
  ASIA1: ['asia-northeast1', 'asia-northeast2'],
  EUR4: ['europe-west4', 'europe-north1'],
  EUR5: ['europe-west1', 'europe-west2'],
  EUR7: ['europe-west2', 'europe-west3'],
  EUR8: ['europe-west3', 'europe-west6'],
  NAM4: ['us-central1', 'us-east1'],
};

export const PREDEFINED_MULTI_REGION_LABELS: readonly string[] = [
  'US',
  'EU',
  'ASIA',
];

export const isPredefinedMultiRegionLabel = (value: string): boolean =>
  PREDEFINED_MULTI_REGION_LABELS.includes(value.toUpperCase());

export const isPredefinedDualRegionLabel = (value: string): boolean =>
  value.toUpperCase() in PREDEFINED_DUAL_REGION_CONSTITUENTS;

export const isPredefinedMultiOrDualRegionLabel = (value: string): boolean =>
  isPredefinedMultiRegionLabel(value) || isPredefinedDualRegionLabel(value);

/**
 * Annotates a region filter value with its location-type suffix:
 *   US   -> "US (multi-region)"
 *   NAM4 -> "NAM4 (dual-region)"
 *   us-east1 / unknown -> unchanged
 *
 * Used by the source-region filter dropdown and policy condition picker so a
 * predefined GCS label is visually distinct from a literal region.
 */
export const formatRegionLabel = (value: string): string => {
  if (isPredefinedMultiRegionLabel(value)) {
    return `${value} (multi-region)`;
  }
  if (isPredefinedDualRegionLabel(value)) {
    return `${value} (dual-region)`;
  }
  return value;
};
